/**
 * Minimal Kiosk Interface
 *
 * Ultra-simple, Apple-inspired design.
 * Maximum 3 elements per screen.
 * No decorative elements.
 * Function over form.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTodaysMenu, useSaladToppings } from '../hooks/useMenu';
import { useBreakfastMenu, usePlaceBreakfastOrder } from '../hooks/useBreakfast';
import { usePlaceLunchKioskOrder, LunchOrderItem } from '../hooks/useLunchKiosk';
import { useOrderingStatus } from '../hooks/useOrderingStatus';
import { useMenuWebSocket } from '../hooks/useMenuWebSocket';
import { TicketRedemption, ItemOptionsOverlay, SaladBuilderOverlay, EmployeeNumberLookup } from '../components/kiosk';
import type { ItemOptionsPayload } from '../hooks/useMenu';

type MealType = 'breakfast' | 'lunch';
type Step = 'start' | 'number' | 'verify' | 'guest' | 'meal' | 'menu' | 'done';

// A lunch item waiting on an options pick before it can join the cart
interface PendingItem {
  name: string;
  orderType: 'regular' | 'special';
  menuItemId?: number;
  specialId?: number;
  itemOptions: ItemOptionsPayload;
}

type Overlay =
  | { kind: 'options'; item: PendingItem; editingCartId?: string }
  | { kind: 'salad'; editingCartId?: string; initialToppings?: number[] }
  | null;

// Progress indicator component
function ProgressIndicator({ currentStep }: { currentStep: Step }) {
  // Don't show on screens that have their own header or start/done
  if (['start', 'number', 'guest', 'done'].includes(currentStep)) return null;

  const getStepNumber = () => {
    switch (currentStep) {
      case 'verify': return 1;
      case 'meal': return 2;
      case 'menu': return 2;
      default: return 0;
    }
  };

  const stepNum = getStepNumber();
  if (stepNum === 0) return null;

  const steps = ['Identify', 'Select Meal', 'Confirm'];

  return (
    <div className="absolute top-6 left-1/2 -translate-x-1/2 flex items-center gap-3">
      {steps.map((label, idx) => (
        <div key={label} className="flex items-center gap-3">
          <div className="flex flex-col items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-semibold transition-all
              ${idx + 1 < stepNum ? 'bg-green-500 text-white' :
                idx + 1 === stepNum ? 'bg-[#10348e] text-white ring-4 ring-[#10348e]/30' :
                'bg-gray-200 text-gray-400'}`}>
              {idx + 1 < stepNum ? '✓' : idx + 1}
            </div>
            <span className={`text-sm mt-1 font-medium ${idx + 1 <= stepNum ? 'text-gray-700' : 'text-gray-400'}`}>
              {label}
            </span>
          </div>
          {idx < steps.length - 1 && (
            <div className={`w-16 h-1 rounded-full -mt-5 ${idx + 1 < stepNum ? 'bg-green-500' : 'bg-gray-200'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

interface Employee {
  name: string;
  number: string | null;
  isGuest: boolean;
}

interface CartItem {
  id: string;
  name: string;
  quantity?: number;
  category?: string;
  optionIds?: number[];
  optionNames?: string[];
  toppingIds?: number[];
  toppingNames?: string[];
  [key: string]: any;
}

// Cart-row subtitle: the option pick, or the first few salad toppings
function cartSubtitle(item: CartItem): string | null {
  if (item.optionNames?.length) return item.optionNames.join(', ');
  if (item.toppingNames?.length) {
    const first = item.toppingNames.slice(0, 3).join(', ');
    const rest = item.toppingNames.length - 3;
    return rest > 0 ? `${first} +${rest}` : first;
  }
  return null;
}

export function KioskPage() {
  const [step, setStep] = useState<Step>('start');
  const [mealType, setMealType] = useState<MealType | null>(null);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [overlay, setOverlay] = useState<Overlay>(null);

  // Timeout warning state
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [timeoutCountdown, setTimeoutCountdown] = useState(10);

  // Animation state for screen transitions
  const [isTransitioning] = useState(false);

  // Hooks
  const { menu: lunchMenu, refetch: refetchLunchMenu } = useTodaysMenu();
  const { data: breakfastMenu } = useBreakfastMenu();
  const { toppings: saladToppings } = useSaladToppings();
  const placeBreakfastOrder = usePlaceBreakfastOrder();
  const placeLunchOrder = usePlaceLunchKioskOrder();
  const { data: orderingStatus } = useOrderingStatus();

  // Real-time menu updates via WebSocket
  const handleMenuUpdate = useCallback(() => {
    console.log('[Kiosk] Menu update received, refreshing...');
    refetchLunchMenu();
  }, [refetchLunchMenu]);

  useMenuWebSocket({ onMenuUpdate: handleMenuUpdate });

  // Inactivity timeout - show warning after 45 seconds, reset after 10 more
  useEffect(() => {
    // Don't run timeout on start or done screens
    if (step === 'start' || step === 'done') return;

    let warningTimeoutId: ReturnType<typeof setTimeout>;
    let countdownIntervalId: ReturnType<typeof setInterval>;

    const resetTimer = () => {
      // Clear any existing timers
      clearTimeout(warningTimeoutId);
      clearInterval(countdownIntervalId);
      setShowTimeoutWarning(false);
      setTimeoutCountdown(10);

      // Set warning to appear after 45 seconds of inactivity
      warningTimeoutId = setTimeout(() => {
        setShowTimeoutWarning(true);
        setTimeoutCountdown(10);

        // Start countdown
        countdownIntervalId = setInterval(() => {
          setTimeoutCountdown(prev => {
            if (prev <= 1) {
              clearInterval(countdownIntervalId);
              resetKiosk();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }, 45000);
    };

    // Events that count as activity
    const events = ['click', 'touchstart', 'keydown', 'mousemove'];

    // Start the timer
    resetTimer();

    // Reset timer on any activity
    const handleActivity = () => {
      if (showTimeoutWarning) {
        setShowTimeoutWarning(false);
      }
      resetTimer();
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    return () => {
      clearTimeout(warningTimeoutId);
      clearInterval(countdownIntervalId);
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [step, showTimeoutWarning]);

  // Auto-reset after 3 seconds on done screen
  useEffect(() => {
    if (step === 'done') {
      const timer = setTimeout(() => resetKiosk(), 3000);
      return () => clearTimeout(timer);
    }
  }, [step]);

  const resetKiosk = () => {
    setShowTimeoutWarning(false);
    setTimeoutCountdown(10);
    setStep('start');
    setMealType(null);
    setEmployee(null);
    setCart([]);
    setOrderId(null);
    setOverlay(null);
  };

  // Single commit point for the cart: guests replace, employees append.
  // Called by direct taps and by overlay Adds; overlays never see guest state.
  const commitToCart = (newItem: CartItem, editingCartId?: string) => {
    if (editingCartId) {
      setCart(cart.map(c => (c.id === editingCartId ? { ...newItem, id: editingCartId } : c)));
    } else if (employee?.isGuest) {
      setCart([newItem]);
    } else {
      setCart([...cart, newItem]);
    }
    setOverlay(null);
  };

  // Reopen the matching overlay pre-filled to edit a cart row in place
  const openEditFor = (item: CartItem) => {
    if (item.optionIds?.length) {
      const itemOptions = item.orderType === 'regular'
        ? lunchMenu?.regular_lunch?.item_options
        : lunchMenu?.daily_specials?.find(s => s.id === item.specialId)?.item_options;
      if (!itemOptions) return;
      setOverlay({
        kind: 'options',
        editingCartId: item.id,
        item: {
          name: item.name,
          orderType: item.orderType as 'regular' | 'special',
          menuItemId: item.menuItemId,
          specialId: item.specialId,
          itemOptions,
        },
      });
    } else if (item.orderType === 'salad') {
      setOverlay({ kind: 'salad', editingCartId: item.id, initialToppings: item.toppingIds });
    }
  };

  // Play success sound
  const playSuccessSound = () => {
    try {
      // Create a simple beep using Web Audio API
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  const handleEmployeeFound = (displayName: string, employeeNumber: string) => {
    setEmployee({
      name: displayName,
      number: employeeNumber,
      isGuest: false
    });
    setStep('verify');
  };

  const handleTicketRedemption = (guestName: string, ticketCode: string, mealType?: string) => {
    const ticketGuest: Employee = {
      name: guestName,
      number: ticketCode,
      isGuest: true
    };
    setEmployee(ticketGuest);

    // If ticket is for a specific meal type, go directly to that menu
    if (mealType && mealType !== 'any') {
      setMealType(mealType as MealType);
      setStep('menu');
    } else {
      setStep('meal');
    }
  };

  const handlePlaceOrder = async () => {
    if (!employee || cart.length === 0) return;

    // Append "-Guest" to name for guest orders
    const displayName = employee.isGuest ? `${employee.name}-Guest` : employee.name;

    try {
      if (mealType === 'breakfast') {
        const items = cart.map(item => ({
          menu_item_id: item.menuItemId,
          quantity: item.quantity || 1,
        }));
        const result = await placeBreakfastOrder.mutateAsync({
          employee_name: displayName,
          employee_number: employee.isGuest ? null : employee.number,
          is_guest: employee.isGuest,
          items,
          ticket_code: employee.isGuest && employee.number ? employee.number : undefined,
        });
        setOrderId(result.id);
      } else if (mealType === 'lunch') {
        const items: LunchOrderItem[] = cart.map(item => ({
          order_type: item.orderType as 'regular' | 'salad' | 'special',
          menu_item_id: item.menuItemId,
          special_id: item.specialId,
          topping_ids: item.toppingIds,
          option_ids: item.optionIds,
        }));
        console.log('Placing lunch order:', { employee, items });
        const result = await placeLunchOrder.mutateAsync({
          employee_name: displayName,
          employee_number: employee.number,
          is_guest: employee.isGuest,
          orders: items,
          ticket_code: employee.isGuest && employee.number ? employee.number : undefined,
        });
        console.log('Lunch order result:', result);
        setOrderId(result.order_ids[0] ?? 0);
      }
      playSuccessSound();
      setStep('done');
    } catch (error) {
      console.error('Order error:', error);
      // Show error briefly then reset
      alert(`Order failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      resetKiosk();
    }
  };

  return (
    <div className="h-screen bg-white flex flex-col overflow-hidden relative">
      {/* Progress Indicator */}
      <ProgressIndicator currentStep={step} />

      {/* Options / Salad overlays (z-40, under the z-50 timeout warning) */}
      {overlay?.kind === 'options' && (
        <ItemOptionsOverlay
          itemName={overlay.item.name}
          itemOptions={overlay.item.itemOptions}
          initialSelected={overlay.editingCartId ? cart.find(c => c.id === overlay.editingCartId)?.optionIds : undefined}
          confirmLabel={overlay.editingCartId ? 'Update Order' : 'Add to Order'}
          onAdd={(optionIds, optionNames) => {
            commitToCart({
              id: `${overlay.item.orderType}-${Date.now()}`,
              name: overlay.item.name,
              orderType: overlay.item.orderType,
              menuItemId: overlay.item.menuItemId,
              specialId: overlay.item.specialId,
              optionIds,
              optionNames,
            }, overlay.editingCartId);
          }}
          onCancel={() => setOverlay(null)}
        />
      )}
      {overlay?.kind === 'salad' && (
        <SaladBuilderOverlay
          categories={saladToppings?.categories || []}
          initialSelected={overlay.initialToppings}
          confirmLabel={overlay.editingCartId ? 'Update Order' : 'Add Salad to Order'}
          onAdd={(toppingIds, toppingNames) => {
            commitToCart({
              id: `salad-${Date.now()}`,
              name: 'Custom Salad',
              orderType: 'salad',
              menuItemId: lunchMenu?.regular_lunch?.id,
              toppingIds,
              toppingNames,
            }, overlay.editingCartId);
          }}
          onCancel={() => setOverlay(null)}
        />
      )}

      {/* Timeout Warning Modal */}
      {showTimeoutWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white rounded-3xl p-10 text-center max-w-md mx-4 shadow-2xl animate-bounce-in">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-yellow-100 flex items-center justify-center">
              <span className="text-5xl">👋</span>
            </div>
            <h2 className="text-3xl font-semibold text-gray-900 mb-3">Are you still there?</h2>
            <p className="text-xl text-gray-500 mb-6">
              This session will reset in <span className="font-bold text-red-500 text-2xl">{timeoutCountdown}</span> seconds
            </p>
            <button
              onClick={() => setShowTimeoutWarning(false)}
              className="w-full h-16 bg-[#10348e] text-white text-xl font-semibold rounded-2xl
                         hover:bg-[#0d2b73] active:scale-[0.98] transition-all"
            >
              I'm still here!
            </button>
          </div>
        </div>
      )}

      {/* Main content with transition */}
      <div className={`flex-1 flex flex-col transition-opacity duration-150 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
      {/* START SCREEN */}
      {step === 'start' && (
        <>
          {/* Header */}
          <header className="h-20 bg-[#10348e] flex items-center justify-center gap-4">
            <img src="/hmh-logo.png" alt="HMH" className="h-12" />
            <span className="text-white text-2xl font-medium tracking-wide">HOOD MEMORIAL HOSPITAL</span>
          </header>

          {/* Content */}
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="w-full max-w-4xl space-y-8">
              <button
                onClick={() => setStep('number')}
                className="w-full h-64 bg-[#10348e] rounded-3xl
                           hover:bg-[#0d2b73] active:scale-[0.98] transition-all
                           flex items-center justify-center"
              >
                <span className="text-6xl font-light text-white">
                  Employee
                </span>
              </button>

              <button
                onClick={() => setStep('guest')}
                className="w-full h-64 bg-[#1e62ac] rounded-3xl
                           hover:bg-[#10348e] active:scale-[0.98] transition-all
                           flex items-center justify-center"
              >
                <span className="text-6xl font-light text-white">
                  Guest
                </span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* EMPLOYEE NUMBER SCREEN */}
      {step === 'number' && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-[#10348e] text-white py-8 px-6 text-center">
            <h1 className="text-4xl font-semibold">Enter Your Employee Number</h1>
            <p className="text-xl text-white/70 mt-2">Type your full employee number</p>
          </div>

          <div className="flex-1 flex flex-col p-6 overflow-hidden">
            <div className="flex-1 flex items-start justify-center pt-8">
              <div className="w-full max-w-2xl">
                <EmployeeNumberLookup onFound={handleEmployeeFound} />
              </div>
            </div>

            {/* Cancel button */}
            <div className="pt-4">
              <button
                onClick={resetKiosk}
                className="w-full h-16 bg-gray-200 hover:bg-gray-300 active:bg-gray-400
                           text-gray-700 text-xl font-semibold rounded-2xl transition-all"
                style={{ touchAction: 'manipulation' }}
              >
                ← Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VERIFY SCREEN */}
      {step === 'verify' && employee && (
        <div className="flex-1 flex flex-col items-center justify-center p-8">
          <div className="w-full max-w-2xl text-center space-y-12">
            {/* Employee info */}
            <div className="space-y-6">
              <div className="w-28 h-28 mx-auto rounded-full bg-[#10348e] flex items-center justify-center mb-4">
                <span className="text-5xl text-white font-bold">
                  {employee.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="text-5xl font-semibold text-gray-900">
                {employee.name}
              </div>
              <div className="text-2xl text-gray-500 bg-gray-100 inline-block px-6 py-2 rounded-full">
                Employee #{employee.number}
              </div>
            </div>

            {/* Buttons */}
            <div className="space-y-4">
              <button
                onClick={() => {
                  // Check available meals and proceed
                  const breakfastOpen = orderingStatus?.breakfast?.is_open ?? true;
                  const lunchOpen = orderingStatus?.lunch?.is_open ?? true;

                  setCart([]); // Clear cart when starting a new order
                  if (breakfastOpen && !lunchOpen) {
                    setMealType('breakfast');
                    setStep('menu');
                  } else if (!breakfastOpen && lunchOpen) {
                    setMealType('lunch');
                    setStep('menu');
                  } else {
                    setStep('meal');
                  }
                }}
                className="w-full h-24 bg-[#10348e] text-white text-3xl font-medium rounded-2xl
                           hover:bg-[#0d2b73] active:scale-[0.98] active:bg-[#0a2260] transition-all"
              >
                Yes, that's me
              </button>
              <button
                onClick={() => {
                  setEmployee(null);
                  setStep('number');
                }}
                className="w-full h-20 bg-gray-100 hover:bg-gray-200 active:bg-gray-300
                           text-gray-600 text-xl font-medium rounded-2xl transition-all"
              >
                Not me - Go back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GUEST SCREEN */}
      {step === 'guest' && (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-2xl space-y-12">
          <div className="text-center mb-8">
            <h2 className="text-5xl font-light text-gray-900 mb-4">Guest Ticket</h2>
            <p className="text-2xl text-gray-500">Please enter your ticket code</p>
          </div>

          <div className="bg-gray-50 rounded-3xl p-8">
            <TicketRedemption onRedemptionComplete={handleTicketRedemption} />
          </div>

          {/* Back button */}
          <div className="text-center">
            <button
              onClick={resetKiosk}
              className="w-full max-w-md mx-auto h-16 bg-gray-100 hover:bg-gray-200 active:bg-gray-300
                         text-gray-600 text-xl font-medium rounded-2xl transition-all"
            >
              Back
            </button>
          </div>
          </div>
        </div>
      )}

      {/* MEAL CHOICE SCREEN */}
      {step === 'meal' && (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-4xl space-y-8">
            <button
              onClick={() => {
                setCart([]); // Clear cart when switching meal types
                setMealType('breakfast');
                setStep('menu');
              }}
              className="w-full h-64 bg-[#10348e] rounded-3xl
                         hover:bg-[#0d2b73] active:scale-[0.98] transition-all
                         flex items-center justify-center"
            >
              <span className="text-6xl font-light text-white">
                Breakfast
              </span>
            </button>

            <button
              onClick={() => {
                setCart([]); // Clear cart when switching meal types
                setMealType('lunch');
                setStep('menu');
              }}
              className="w-full h-64 bg-[#1e62ac] rounded-3xl
                         hover:bg-[#10348e] active:scale-[0.98] transition-all
                         flex items-center justify-center"
            >
              <span className="text-6xl font-light text-white">
                Lunch
              </span>
            </button>
          </div>
        </div>
      )}

      {/* MENU SCREEN - BREAKFAST */}
      {step === 'menu' && mealType === 'breakfast' && (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-6xl">
          <div className="grid grid-cols-2 gap-8">
            {/* Menu Items */}
            <div className="space-y-6">
              {Object.entries(breakfastMenu?.by_category || {}).map(([category, items]) => {
                const categoryLower = category.toLowerCase();
                const selectedInCategory = cart.find(c => c.category === categoryLower);

                return (
                  <div key={category} className="mb-6">
                    <h3 className="text-2xl font-semibold text-gray-600 mb-4 uppercase tracking-wide">
                      {category}
                    </h3>
                    <div className="space-y-4">
                      {(items as any[]).map((item) => {
                        const isSelected = selectedInCategory?.menuItemId === item.id;

                        return (
                          <button
                            key={item.id}
                            onClick={() => {
                              const existingInCategory = cart.filter(c => c.category === categoryLower);

                              if (existingInCategory.length >= 1) {
                                // Replace existing item in category
                                setCart(cart.map(c =>
                                  c.category === categoryLower
                                    ? { id: `${item.id}-${Date.now()}`, name: item.name, menuItemId: item.id, category: categoryLower, quantity: 1 }
                                    : c
                                ));
                              } else {
                                // Add new item
                                setCart([...cart, {
                                  id: `${item.id}-${Date.now()}`,
                                  name: item.name,
                                  menuItemId: item.id,
                                  category: categoryLower,
                                  quantity: 1,
                                }]);
                              }
                            }}
                            className={`w-full py-5 px-6 rounded-2xl min-h-[72px]
                                       active:scale-[0.98] transition-all text-left
                                       ${isSelected
                                         ? 'bg-[#0d2b73] ring-4 ring-[#10348e] ring-offset-2 shadow-lg'
                                         : 'bg-[#1e62ac] hover:bg-[#10348e] active:bg-[#0a2260]'}`}
                          >
                            <div className="text-2xl font-medium text-white flex items-center justify-between">
                              <span>{item.name}</span>
                              {isSelected && <span className="text-3xl">✓</span>}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Cart */}
            <div className="space-y-4 sticky top-0">
              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden min-h-[400px] flex flex-col">
                {/* Cart Header */}
                <div className="bg-[#10348e] text-white px-6 py-4 flex items-center justify-between">
                  <span className="text-xl font-medium">Your Order</span>
                  {cart.length > 0 && (
                    <span className="bg-white text-[#10348e] text-lg font-bold px-3 py-1 rounded-full">
                      {cart.length}
                    </span>
                  )}
                </div>

                {cart.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center p-6">
                    <span className="text-gray-400 text-xl font-light">
                      No items
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="flex-1 space-y-3 p-4">
                      {cart.map(item => {
                        const subtitle = cartSubtitle(item);
                        return (
                          <div key={item.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                            <button
                              onClick={() => openEditFor(item)}
                              className="flex-1 text-left min-w-0 pr-3"
                            >
                              <span className="block text-xl font-medium text-gray-900 truncate">
                                {item.name}
                              </span>
                              {subtitle && (
                                <span className="block text-base text-gray-500 truncate">{subtitle}</span>
                              )}
                            </button>
                            <button
                              onClick={() => setCart(cart.filter(c => c.id !== item.id))}
                              className="w-12 h-12 shrink-0 flex items-center justify-center bg-red-100 hover:bg-red-200
                                         active:bg-red-300 text-red-600 text-2xl font-bold rounded-xl transition-all"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    <div className="p-4">
                      <button
                        onClick={handlePlaceOrder}
                        className="w-full h-20 bg-[#10348e] rounded-2xl
                                   hover:bg-[#0d2b73] active:scale-[0.98] active:bg-[#0a2260] transition-all
                                   text-2xl font-semibold text-white"
                      >
                        Place Order
                      </button>
                    </div>
                  </>
                )}
              </div>
              <button
                onClick={resetKiosk}
                className="w-full h-14 bg-gray-100 hover:bg-gray-200 active:bg-gray-300
                           text-gray-600 text-lg font-medium rounded-2xl transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
          </div>
        </div>
      )}

      {/* MENU SCREEN - LUNCH */}
      {step === 'menu' && mealType === 'lunch' && (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-6xl">
          <div className="grid grid-cols-2 gap-8">
            {/* Menu Items */}
            <div className="space-y-4">
              {lunchMenu?.regular_lunch && lunchMenu?.show_regular_lunch !== false && (
                <button
                  onClick={() => {
                    const opts = lunchMenu.regular_lunch!.item_options;
                    if (opts && opts.options.length > 0) {
                      setOverlay({
                        kind: 'options',
                        item: {
                          name: lunchMenu.regular_lunch!.name,
                          orderType: 'regular',
                          menuItemId: lunchMenu.regular_lunch!.id,
                          itemOptions: opts,
                        },
                      });
                      return;
                    }
                    commitToCart({
                      id: `regular-${Date.now()}`,
                      name: lunchMenu.regular_lunch!.name,
                      orderType: 'regular',
                      menuItemId: lunchMenu.regular_lunch!.id,
                    });
                  }}
                  className="w-full py-6 px-6 bg-[#10348e] rounded-2xl min-h-[80px]
                             hover:bg-[#0d2b73] active:scale-[0.98] active:bg-[#0a2260] transition-all
                             text-left shadow-md"
                >
                  <div className="text-2xl font-medium text-white">
                    {lunchMenu.regular_lunch.name}
                  </div>
                  {lunchMenu.regular_lunch.description && (
                    <div className="text-lg text-white/70 mt-1">
                      {lunchMenu.regular_lunch.description}
                    </div>
                  )}
                  {(lunchMenu.regular_lunch.item_options?.options.length ?? 0) > 0 && (
                    <div className="mt-2 text-right">
                      <span className="inline-block bg-white/20 text-white text-base font-medium px-3 py-1 rounded-full">
                        {lunchMenu.regular_lunch.item_options!.options.length} choices ›
                      </span>
                    </div>
                  )}
                </button>
              )}

              {lunchMenu?.daily_specials?.map((special, index) => (
                <button
                  key={special.id || index}
                  onClick={() => {
                    if (special.item_options && special.item_options.options.length > 0) {
                      setOverlay({
                        kind: 'options',
                        item: {
                          name: special.name,
                          orderType: 'special',
                          specialId: special.id,
                          itemOptions: special.item_options,
                        },
                      });
                      return;
                    }
                    commitToCart({
                      id: `special-${Date.now()}-${index}`,
                      name: special.name,
                      orderType: 'special',
                      specialId: special.id,
                    });
                  }}
                  className="w-full py-6 px-6 bg-[#1e62ac] rounded-2xl min-h-[80px]
                             hover:bg-[#10348e] active:scale-[0.98] active:bg-[#0a2260] transition-all
                             text-left shadow-md"
                >
                  <div className="text-2xl font-medium text-white flex items-center gap-3">
                    <span className="text-yellow-400">★</span>
                    {special.name}
                  </div>
                  {special.description && (
                    <div className="text-lg text-white/70 mt-1 ml-8">
                      {special.description}
                    </div>
                  )}
                  {(special.item_options?.options.length ?? 0) > 0 && (
                    <div className="mt-2 text-right">
                      <span className="inline-block bg-white/20 text-white text-base font-medium px-3 py-1 rounded-full">
                        {special.item_options!.options.length} choices ›
                      </span>
                    </div>
                  )}
                </button>
              ))}

              {lunchMenu?.salad_available && (
                <button
                  onClick={() => setOverlay({ kind: 'salad' })}
                  className="w-full py-6 px-6 bg-[#2980b9] rounded-2xl min-h-[80px]
                             hover:bg-[#1e62ac] active:scale-[0.98] active:bg-[#10348e] transition-all
                             text-left shadow-md"
                >
                  <div className="text-2xl font-medium text-white flex items-center gap-3">
                    <span className="text-green-400">🥗</span>
                    Build Your Salad
                  </div>
                  <div className="text-lg text-white/70 mt-1 ml-8">
                    Choose your own toppings
                  </div>
                </button>
              )}
            </div>

            {/* Cart */}
            <div className="space-y-4 sticky top-0">
              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden min-h-[400px] flex flex-col">
                {/* Cart Header */}
                <div className="bg-[#10348e] text-white px-6 py-4 flex items-center justify-between">
                  <span className="text-xl font-medium">Your Order</span>
                  {cart.length > 0 && (
                    <span className="bg-white text-[#10348e] text-lg font-bold px-3 py-1 rounded-full">
                      {cart.length}
                    </span>
                  )}
                </div>

                {cart.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center p-6">
                    <span className="text-gray-400 text-xl font-light">
                      No items
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="flex-1 space-y-3 p-4">
                      {cart.map(item => {
                        const subtitle = cartSubtitle(item);
                        return (
                          <div key={item.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                            <button
                              onClick={() => openEditFor(item)}
                              className="flex-1 text-left min-w-0 pr-3"
                            >
                              <span className="block text-xl font-medium text-gray-900 truncate">
                                {item.name}
                              </span>
                              {subtitle && (
                                <span className="block text-base text-gray-500 truncate">{subtitle}</span>
                              )}
                            </button>
                            <button
                              onClick={() => setCart(cart.filter(c => c.id !== item.id))}
                              className="w-12 h-12 shrink-0 flex items-center justify-center bg-red-100 hover:bg-red-200
                                         active:bg-red-300 text-red-600 text-2xl font-bold rounded-xl transition-all"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    <div className="p-4">
                      <button
                        onClick={handlePlaceOrder}
                        className="w-full h-20 bg-[#10348e] rounded-2xl
                                   hover:bg-[#0d2b73] active:scale-[0.98] active:bg-[#0a2260] transition-all
                                   text-2xl font-semibold text-white"
                      >
                        Place Order
                      </button>
                    </div>
                  </>
                )}
              </div>
              <button
                onClick={resetKiosk}
                className="w-full h-14 bg-gray-100 hover:bg-gray-200 active:bg-gray-300
                           text-gray-600 text-lg font-medium rounded-2xl transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
          </div>
        </div>
      )}

      {/* DONE SCREEN */}
      {step === 'done' && (
        <div className="flex-1 flex flex-col items-center justify-center p-8 relative overflow-hidden">
          {/* Celebration particles */}
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className="absolute w-4 h-4 rounded-full animate-float-up"
                style={{
                  left: `${10 + (i * 7)}%`,
                  backgroundColor: ['#10348e', '#22c55e', '#eab308', '#ef4444', '#8b5cf6'][i % 5],
                  animationDelay: `${i * 0.1}s`,
                  animationDuration: `${1.5 + (i % 3) * 0.3}s`,
                }}
              />
            ))}
          </div>

          <div className="text-center space-y-10 relative z-10">
            <div className="w-40 h-40 mx-auto rounded-full bg-green-500 flex items-center justify-center
                            shadow-lg shadow-green-500/30 animate-success-pop">
              <span className="text-7xl text-white">✓</span>
            </div>
            <div className="space-y-4 animate-fade-in-up">
              <div className="text-6xl font-medium text-gray-900">
                Thank you!
              </div>
              <div className="text-3xl text-gray-600">
                {employee?.name}
              </div>
              <div className="text-2xl text-gray-500">
                Your {mealType} order has been placed
              </div>
              <div className="text-xl text-gray-400 mt-4">
                Order #{orderId}
              </div>
            </div>
            <button
              onClick={resetKiosk}
              className="px-16 py-6 bg-[#10348e] text-white text-2xl font-semibold rounded-2xl
                         hover:bg-[#0d2b73] active:scale-[0.98] active:bg-[#0a2260] transition-all
                         shadow-lg animate-fade-in"
            >
              Place Another Order
            </button>
          </div>
        </div>
      )}
      </div>
      <footer className="absolute bottom-0 inset-x-0 h-10 bg-gray-50 border-t flex items-center justify-center px-4 z-10">
        <p className="text-xs text-gray-700">By placing this order, you voluntarily authorize payroll deduction of the meal(s) selected.</p>
      </footer>
    </div>
  );
}
